import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Carousel from 'nuka-carousel';
import { useAuthentication, useSSRQuery } from '../../graphql';
import { ConditionalWrap } from '../../components/Boneyard';
import tracking from '@g4/g4-analytics';
import { TrackClick } from '@g4/trackster';
import BILLBOARD_CONFIGURATION_QUERY from './billboardConfiguration/billboardConfiguration.query.graphql';
import LEFT_ARROW from '../../images/icon-45-arrow-left-blue.svg';
import RIGHT_ARROW from '../../images/icon-45-arrow-right-blue.svg';
import { BILLBOARD_CAROUSEL } from '../../../automationHooks.json';
import { isInternalUrl } from '../../utils/routeUtils';

const propTypes = {
  updateHeroImage: PropTypes.func.isRequired,
  updateDisclaimer: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  selectedOrigin: PropTypes.string,
  selectedDestination: PropTypes.string,
};

const BillboardWrapper = styled.div`
  margin: 0;
  text-align: center;
  padding: ${props => props.theme.spacing(6)} ${props => props.theme.spacing(4)};
  .slider-frame {
    margin-top: 0 !important;
  }
  ${props => props.theme.query.md} {
    padding: 0
      ${props => (props.isSingleBillboard ? props.theme.spacing(20) : 0)}
      ${props => props.theme.spacing(10)};
  }
  .slider-control-bottomcenter button:focus {
    outline: 2px solid ${props => props.theme.colors.common.dark};
  }
`;

const ArrowButton = styled.button`
  display: ${props => (props.hide ? 'none' : 'inherit')};
  &:focus {
    ${props => props.theme.focusStyle}
  }
`;

const LinkWrapper = styled.div`
  padding: 0;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${props => props.theme.query.md} {
    min-height: 180px;
    max-height: 220px;
  }
`;

const BillboardConfiguration = ({
  updateHeroImage,
  updateDisclaimer,
  isMobile,
  selectedOrigin,
  selectedDestination,
}) => {
  const { loading, error, data, refetch } = useSSRQuery(
    BILLBOARD_CONFIGURATION_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        billboardSearchCriteria: {
          originTag: selectedOrigin ? selectedOrigin : '',
          destinationTag: selectedDestination ? selectedDestination : '',
        },
      },
    }
  );
  const [slideIndex, setSlideIndex] = useState(0);
  const [trackedPromos, setTrackedPromos] = useState([]);
  const { isAuthenticated } = useAuthentication();
  const [isCarouselPaused, setCarouselPaused] = useState(false);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Tab') {
        const slider = document.querySelector('.slider');
        if (slider && slider.contains(document.activeElement)) {
          setCarouselPaused(true);
        } else {
          setCarouselPaused(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  React.useEffect(() => {
    refetch && refetch();
  }, [isAuthenticated, refetch]);

  const trackBillboardImpression = useCallback(
    billboard => {
      if (isInternalUrl(billboard.clickThrough?.url)) {
        const viewPromoImpressionData = {
          creative: !isMobile
            ? billboard.overlay.image.split('/').slice(-1)[0]
            : billboard.heroMobileImage.image.split('/').slice(-1)[0],
          name: billboard.overlay.title,
          position: `Billboard ${slideIndex + 1}-HomePage`,
          id: '',
          items: [
            {
              item_id: '',
              item_name: billboard.overlay.title,
              coupon: '',
              discount: '',
              index: '',
              affiliation: 'WWW - Home',
              item_brand: 'G4',
              item_category: '',
              item_category2: '',
              item_category3: '',
              item_category4: '',
              item_category5: '',
              price: '',
              quantity: '',
              promotion_name: billboard.overlay.title,
              promotion_id: '',
            },
          ],
        };
        tracking.trackPromoView(viewPromoImpressionData);
      } else {
        const externalPromoImpressionData = {
          name: billboard.overlay.title,
          creative: !isMobile
            ? billboard.overlay.image.split('/').slice(-1)[0]
            : billboard.heroMobileImage.image.split('/').slice(-1)[0],
          position: `Billboard ${slideIndex + 1}`,
        };

        tracking.trackPromoImpressionExternal(externalPromoImpressionData);
      }
      setTrackedPromos([...trackedPromos, slideIndex]);
    },
    [setTrackedPromos, trackedPromos, slideIndex, isMobile]
  );

  useEffect(() => {
    if (loading) {
      setSlideIndex(0);
    }
    if (!loading && data && data.application) {
      const { billboardConfigurations } = data.application;
      if (billboardConfigurations.billboards.length) {
        updateHeroImage(
          billboardConfigurations.billboards[slideIndex].heroImage
        );
        updateDisclaimer(
          billboardConfigurations.billboards[slideIndex].disclaimer
        );

        const timeOut = setTimeout(() => {
          if (!trackedPromos.includes(slideIndex)) {
            trackBillboardImpression(
              billboardConfigurations.billboards[slideIndex]
            );
          }
        }, 100);
        return () => clearTimeout(timeOut);
      }
    }
  }, [
    data,
    loading,
    slideIndex,
    updateDisclaimer,
    updateHeroImage,
    trackedPromos,
    trackBillboardImpression,
  ]);

  if (loading) {
    return <BillboardWrapper />;
  }

  if (error) {
    console.log(error);
    return null;
  }

  const { billboardConfigurations } = data.application;
  if (!billboardConfigurations) {
    return null;
  }
  if (!billboardConfigurations.billboards) {
    return null;
  }
  const isSingleBillboard = billboardConfigurations.billboards.length <= 1;

  const trackPromoClick = b => {
    if (isInternalUrl(b.clickThrough?.url)) {
      const viewPromoImpressionData = {
        creative: !isMobile
          ? b.overlay.image.split('/').slice(-1)[0]
          : b.heroMobileImage.image.split('/').slice(-1)[0],
        name: b.overlay.title,
        position: `Billboard ${slideIndex + 1}-HomePage`,
        id: '',
        items: [
          {
            item_id: '',
            item_name: '',
            coupon: '',
            discount: '',
            index: '',
            affiliation: 'WWW - Home',
            item_brand: 'G4',
            item_category: '',
            item_category2: '',
            item_category3: '',
            item_category4: '',
            item_category5: '',
            price: '',
            quantity: '',
            promotion_name: b.overlay.title,
            promotion_id: '',
          },
        ],
      };
      tracking.trackPromoSelect(viewPromoImpressionData);
    } else {
      const externalPromoImpressionData = {
        name: b.overlay.title,
        creative: !isMobile
          ? b.overlay.image.split('/').slice(-1)[0]
          : b.heroMobileImage.image.split('/').slice(-1)[0],
        position: `Billboard ${slideIndex + 1}`,
      };

      tracking.trackPromoClickExternal(externalPromoImpressionData);
    }
  };

  return (
    <BillboardWrapper
      data-hook={BILLBOARD_CAROUSEL.hook}
      isSingleBillboard={isSingleBillboard}
    >
      <ConditionalWrap
        condition={!isSingleBillboard}
        wrap={children => (
          <Carousel
            speed={300}
            autoplay={
              !isCarouselPaused &&
              !!billboardConfigurations.billboardCycleDelay &&
              billboardConfigurations.billboards.length > 1
            }
            autoplayInterval={7000}
            // making framePadding null to get default to padding for resposive view
            framePadding={!isMobile ? '0 100px' : null}
            wrapAround={true}
            heightMode={'max'}
            afterSlide={slideIndex => setSlideIndex(slideIndex)}
            withoutControls={isSingleBillboard}
            defaultControlsConfig={{
              pagingDotsStyle: {
                position: 'relative',
                bottom: '-30px',
                width: 20,
                fill: '#01579b',
              },
            }}
            renderCenterLeftControls={({ previousSlide }) => (
              <TrackClick
                action="Billboard Selection"
                category="site_click_tracking"
                label="Previous Page"
              >
                <ArrowButton
                  hide={isMobile || isSingleBillboard}
                  onClick={previousSlide}
                  onFocus={() => setCarouselPaused(false)}
                >
                  <img alt="left arrow" src={LEFT_ARROW} />
                </ArrowButton>
              </TrackClick>
            )}
            renderCenterRightControls={({ nextSlide }) => (
              <TrackClick
                action="Billboard Selection"
                category="site_click_tracking"
                label="Next Page"
              >
                <ArrowButton
                  hide={isMobile || isSingleBillboard}
                  onClick={nextSlide}
                  onFocus={() => setCarouselPaused(false)}
                >
                  <img alt="right arrow" src={RIGHT_ARROW} />
                </ArrowButton>
              </TrackClick>
            )}
          >
            {children}
          </Carousel>
        )}
      >
        {billboardConfigurations.billboards.map(b => (
          <LinkWrapper key={b.title}>
            <a
              href={b.clickThrough?.url}
              target={'_' + b.clickThrough?.target.toLowerCase()}
              title={b.clickThrough?.title}
              onClick={() => trackPromoClick(b)}
            >
              <img
                key={b.overlay.image}
                src={!isMobile ? b.overlay.image : b.heroMobileImage.image}
                alt={b.overlay.alternateText}
                title={b.overlay.title}
              />
            </a>
          </LinkWrapper>
        ))}
      </ConditionalWrap>
    </BillboardWrapper>
  );
};
BillboardConfiguration.propTypes = propTypes;
export default BillboardConfiguration;
